import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import otpService from '../../services/otpService';
import './Auth.css';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    phoneCode: '+91',
    role: 'candidate'
  });

  const [otpState, setOtpState] = useState({
    phoneOtpSent: false,
    phoneOtpVerified: false,
    phoneOtp: '',
    phoneVerificationToken: '',
    sendingPhoneOtp: false,
    verifyingPhoneOtp: false,

    emailOtpSent: false,
    emailOtpVerified: false,
    emailOtp: '',
    emailVerificationToken: '',
    sendingEmailOtp: false,
    verifyingEmailOtp: false,
    canResendEmail: true,
    emailTimer: 0
  });

  // Countdown for email OTP resend
  useEffect(() => {
    let interval;
    if (otpState.emailTimer > 0) {
      interval = setInterval(() => {
        setOtpState((prev) => {
          if (prev.emailTimer <= 1) {
            return { ...prev, emailTimer: 0, canResendEmail: true };
          }
          return { ...prev, emailTimer: prev.emailTimer - 1 };
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpState.emailTimer]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

    if (e.target.name === 'phone' && otpState.phoneOtpVerified) {
      setOtpState(prev => ({
        ...prev,
        phoneOtpVerified: false,
        phoneOtpSent: false,
        phoneVerificationToken: ''
      }));
    }

    if (e.target.name === 'email') {
      setOtpState(prev => ({
        ...prev,
        emailOtpSent: false,
        emailOtpVerified: false,
        emailOtp: '',
        emailVerificationToken: '',
        canResendEmail: true,
        emailTimer: 0
      }));
    }
  };

  const handleSendPhoneOtp = async () => {
    if (!formData.phone || formData.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setOtpState(prev => ({ ...prev, sendingPhoneOtp: true }));
    try {
      const response = await otpService.sendOTP({
        phone: formData.phone,
        phoneCode: formData.phoneCode
      });
      toast.success(response.message || 'OTP sent to your phone');
      setOtpState(prev => ({ ...prev, phoneOtpSent: true, sendingPhoneOtp: false }));
    } catch (error) {
      toast.error(error.message || 'Failed to send OTP');
      setOtpState(prev => ({ ...prev, sendingPhoneOtp: false }));
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (!otpState.phoneOtp || otpState.phoneOtp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setOtpState(prev => ({ ...prev, verifyingPhoneOtp: true }));
    try {
      const response = await otpService.verifyOTP({
        phone: formData.phone,
        phoneCode: formData.phoneCode,
        otp: otpState.phoneOtp
      });
      toast.success(response.message || 'Phone verified successfully');
      setOtpState(prev => ({
        ...prev,
        phoneOtpVerified: true,
        verifyingPhoneOtp: false,
        phoneVerificationToken: response.verificationToken
      }));
    } catch (error) {
      toast.error(error.message || 'Invalid OTP');
      setOtpState(prev => ({ ...prev, verifyingPhoneOtp: false }));
    }
  };

  const handleSendEmailOtp = async () => {
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    setOtpState(prev => ({ ...prev, sendingEmailOtp: true }));
    try {
      const serviceCall = otpState.emailOtpSent ? otpService.resendOTP : otpService.sendOTP;
      const response = await serviceCall({ email: formData.email });
      toast.success(response.message || 'OTP sent to your email');
      setOtpState(prev => ({
        ...prev,
        emailOtpSent: true,
        sendingEmailOtp: false,
        canResendEmail: false,
        emailTimer: 60
      }));
    } catch (error) {
      toast.error(error.message || 'Failed to send OTP');
      setOtpState(prev => ({ ...prev, sendingEmailOtp: false }));
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!otpState.emailOtp || otpState.emailOtp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setOtpState(prev => ({ ...prev, verifyingEmailOtp: true }));
    try {
      const response = await otpService.verifyOTP({
        email: formData.email,
        otp: otpState.emailOtp
      });
      toast.success(response.message || 'Email verified successfully');
      setOtpState(prev => ({
        ...prev,
        emailOtpVerified: true,
        verifyingEmailOtp: false,
        emailVerificationToken: response.verificationToken
      }));
    } catch (error) {
      toast.error(error.message || 'Invalid OTP');
      setOtpState(prev => ({ ...prev, verifyingEmailOtp: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otpState.phoneOtpVerified) {
      toast.error('Please verify your phone number first');
      return;
    }

    if (!otpState.phoneVerificationToken) {
      toast.error('Verification token missing. Please verify your phone again.');
      return;
    }

    if (!otpState.emailOtpVerified) {
      toast.error('Please verify your email address first');
      return;
    }

    if (!otpState.emailVerificationToken) {
      toast.error('Email verification token missing. Please verify your email again.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      await register({
        ...registerData,
        phoneVerificationToken: otpState.phoneVerificationToken,
        emailVerificationToken: otpState.emailVerificationToken
      });
      toast.success('Registration successful! Redirecting to dashboard...');

      setTimeout(() => {
        const dashboardRoute =
          formData.role === 'employer' ? '/employer/dashboard' :
            formData.role === 'consultant' ? '/consultant/dashboard' :
              '/candidate/dashboard';
        navigate(dashboardRoute);
      }, 1500);
    } catch (error) {
      console.log(error);
      const errorMessage = error?.error?.message || 'Registration failed';
      toast.error(errorMessage);

      // If email/phone already exists, let the user re-enter and re-verify
      if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('already')) {
        setOtpState(prev => ({
          ...prev,
          phoneOtpVerified: false,
          phoneOtpSent: false,
          phoneOtp: '',
          phoneVerificationToken: '',
          emailOtpVerified: false,
          emailOtpSent: false,
          emailOtp: '',
          emailVerificationToken: '',
          canResendEmail: true,
          emailTimer: 0
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: 'candidate', icon: 'person_search', label: 'Job Seeker' },
    { value: 'employer', icon: 'business', label: 'Employer' },
    { value: 'consultant', icon: 'support_agent', label: 'Consultant' },
  ];

  return (
    <div className="auth-page">
      {/* Left – Branding */}
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-brand-icon">
            <span className="material-symbols-outlined">work</span>
          </div>
          <span className="auth-brand-name">HiringOpen</span>
        </div>
        <h1 className="auth-left-headline">Start Your<br />Journey Today</h1>
        <p className="auth-left-sub">Join thousands of professionals finding their dream jobs and top companies hiring the best talent.</p>
        <div className="auth-features">
          {[
            { icon: 'business', title: 'Top Companies', desc: 'Connect with leading employers worldwide' },
            { icon: 'smart_toy', title: 'Smart Matching', desc: 'AI-powered job recommendations for you' },
            { icon: 'rocket_launch', title: 'Easy Apply', desc: 'One-click applications to any job' },
            { icon: 'notifications', title: 'Live Alerts', desc: 'Stay updated on new opportunities' },
          ].map(f => (
            <div key={f.title} className="auth-feature">
              <div className="auth-feature-icon">
                <span className="material-symbols-outlined">{f.icon}</span>
              </div>
              <div className="auth-feature-text">
                <h4>{f.title}</h4>
                <p>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right – Form */}
      <div className="auth-right">
        <Link to="/" className="auth-back-link">
          <span className="material-symbols-outlined">arrow_back</span>
          Back to Home
        </Link>
        <div className="auth-form-wrap">
          <div className="auth-form-icon">
            <span className="material-symbols-outlined">person_add</span>
          </div>
          <h1 className="auth-form-title">Create Account</h1>
          <p className="auth-form-subtitle">Fill in your details and verify both phone and email</p>

          {/* Role selector */}
          <div className="auth-role-selector">
            {roleOptions.map(r => (
              <button
                key={r.value}
                type="button"
                className={`auth-role-btn ${formData.role === r.value ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, role: r.value })}
              >
                <span className="material-symbols-outlined">{r.icon}</span>
                <span>{r.label}</span>
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {/* Name */}
            <div className="auth-field">
              <label className="auth-label">
                <span className="material-symbols-outlined">person</span>Full Name *
              </label>
              <input type="text" name="name" className="auth-input" placeholder="John Doe"
                value={formData.name} onChange={handleChange} required />
            </div>

            {/* Email */}
            <div className="auth-field">
              <label className="auth-label">
                <span className="material-symbols-outlined">email</span>Email *
              </label>
              <div className="auth-phone-row">
                <div className="auth-phone-input-wrap">
                  <input type="email" name="email" className="auth-input" placeholder="john@example.com"
                    value={formData.email} onChange={handleChange} required
                    disabled={otpState.emailOtpVerified} />
                </div>
                {!otpState.emailOtpVerified ? (
                  <button type="button" className="auth-otp-btn"
                    onClick={handleSendEmailOtp}
                    disabled={!formData.email || otpState.sendingEmailOtp || !otpState.canResendEmail}>
                    {otpState.sendingEmailOtp
                      ? '…'
                      : !otpState.canResendEmail
                        ? `${otpState.emailTimer}s`
                        : otpState.emailOtpSent
                          ? 'Resend'
                          : 'Send'}
                  </button>
                ) : (
                  <span className="auth-otp-verified">
                    <span className="material-symbols-outlined">check_circle</span>Verified
                  </span>
                )}
              </div>
              {otpState.emailOtpSent && !otpState.emailOtpVerified && (
                <div className="auth-otp-hint" style={{ marginTop: '8px' }}>
                  <span className="material-symbols-outlined">info</span>
                  Check your email for the verification code
                </div>
              )}
            </div>

            {/* Email OTP */}
            {otpState.emailOtpSent && !otpState.emailOtpVerified && (
              <div className="auth-field">
                <label className="auth-label">
                  <span className="material-symbols-outlined">key</span>Email Verification Code
                </label>
                <div className="auth-phone-row">
                  <div className="auth-phone-input-wrap">
                    <input type="text" className="auth-input" placeholder="6-digit Code"
                      value={otpState.emailOtp} maxLength={6}
                      onChange={(e) => setOtpState(prev => ({ ...prev, emailOtp: e.target.value }))} />
                  </div>
                  <button type="button" className="auth-otp-btn"
                    onClick={handleVerifyEmailOtp}
                    disabled={otpState.verifyingEmailOtp || otpState.emailOtp.length !== 6}>
                    {otpState.verifyingEmailOtp ? '…' : 'Verify'}
                  </button>
                </div>
              </div>
            )}

            {/* Password + Confirm */}
            <div className="auth-form-row">
              <div className="auth-field">
                <label className="auth-label">
                  <span className="material-symbols-outlined">lock</span>Password *
                </label>
                <div className="auth-password-wrap">
                  <input type={showPassword ? 'text' : 'password'} name="password"
                    className="auth-input" placeholder="Min. 8 characters"
                    value={formData.password} onChange={handleChange} required minLength={8} />
                  <button type="button" className="auth-password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    <span className="material-symbols-outlined">{showPassword ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
              </div>
              <div className="auth-field">
                <label className="auth-label">
                  <span className="material-symbols-outlined">lock</span>Confirm Password *
                </label>
                <div className="auth-password-wrap">
                  <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword"
                    className="auth-input" placeholder="Re-enter password"
                    value={formData.confirmPassword} onChange={handleChange} required />
                  <button type="button" className="auth-password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    <span className="material-symbols-outlined">{showConfirmPassword ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Phone */}
            <div className="auth-field">
              <label className="auth-label">
                <span className="material-symbols-outlined">phone</span>Phone Number *
              </label>
              <div className="auth-phone-row">
                <select name="phoneCode" className="auth-input auth-phone-code"
                  value={formData.phoneCode} onChange={handleChange}
                  disabled={otpState.phoneOtpVerified}>
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+971">+971</option>
                </select>
                <div className="auth-phone-input-wrap">
                  <input type="tel" name="phone" className="auth-input" placeholder="9876543210"
                    value={formData.phone} onChange={handleChange} required
                    disabled={otpState.phoneOtpVerified} />
                  {!otpState.phoneOtpVerified ? (
                    <button type="button" className="auth-otp-btn"
                      onClick={handleSendPhoneOtp}
                      disabled={otpState.phoneOtpSent || otpState.sendingPhoneOtp}>
                      {otpState.sendingPhoneOtp ? '…' : otpState.phoneOtpSent ? 'Resend' : 'Send'}
                    </button>
                  ) : (
                    <span className="auth-otp-verified">
                      <span className="material-symbols-outlined">check_circle</span>Verified
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Phone OTP entry */}
            {otpState.phoneOtpSent && !otpState.phoneOtpVerified && (
              <div className="auth-field">
                <label className="auth-label">
                  <span className="material-symbols-outlined">key</span>Enter Verification Code
                </label>
                <div className="auth-phone-row">
                  <input type="text" className="auth-input" placeholder="6-digit Code"
                    value={otpState.phoneOtp} maxLength={6}
                    onChange={(e) => setOtpState(prev => ({ ...prev, phoneOtp: e.target.value }))} />
                  <button type="button" className="auth-otp-btn"
                    onClick={handleVerifyPhoneOtp}
                    disabled={otpState.verifyingPhoneOtp || otpState.phoneOtp.length !== 6}>
                    {otpState.verifyingPhoneOtp ? '…' : 'Verify'}
                  </button>
                </div>
              </div>
            )}

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading
                ? <><div className="auth-spinner"></div>Creating Account…</>
                : <>Create Account<span className="material-symbols-outlined">arrow_forward</span></>
              }
            </button>
          </form>

          <div className="auth-bottom-link">
            Already have an account? <Link to="/login">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
